import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import usePostalCode from "../../../../hooks/usePostalCode";
import { Row, Col, notification, Select, Form } from "antd";
import PostalCodeAsyncTextInput from "../../CommonFormItems/PostalCodeAsyncTextInput/PostalCodeAsyncTextInput";
import TextInput from "../../CommonFormItems/TextInput/TextInput";
import { userProfiles } from "../../../../redux/slices/UserProfilesSlice";

import {
  checkAuthorisedPostalCode,
  checkSpecialDestination,
} from "../../../../utils/postalCodeUtils";
import { updateParticipantsDetails } from "../../../../redux/slices/ParticipantsDetailsSlice";
import { canUseSpecialPostCode } from "../../../../utils/userRolesUtils";
import { participantsDetails } from "../../../../redux/slices/ParticipantsDetailsSlice";

const { Option } = Select;

export default function ParticipantPostalCodeFieldsBlock({
  initialFormValues,
  form,
  externalLoadingSetter = () => {},
  participantType,
  isPreregister = false,
  isNewParticipant = false,
  getFieldsValue,
  proximityService,
  getPostalCodeRestriction,
  onPostalCodeValidated = () => {},
  disabled = false,
}) {
  const dispatch = useDispatch();
  const reduxParticipantDetails = useSelector(participantsDetails);
  const { data: reduxUserProfiles } = useSelector(userProfiles);
  const { setFieldsValue, setFields, getFieldDecorator } = form;

  const [isLoading, setIsLoading] = useState(false);
  const [cityChoices, setCityChoices] = useState([]);
  const [hasMultipleCities, setHasMultipleCities] = useState(false);

  const { getPostalCodeInfo } = usePostalCode();

  const disablePostalCodeField = (initialFieldValue, participantType) => {
    if (disabled) return true;

    if (participantType === "recipient") {
      if (initialFieldValue && !isNewParticipant) return true;
      if (isNewParticipant) return false;
      return false;
    }

    if (participantType === "sender") {
      if (initialFieldValue && !isNewParticipant) return true;
      if (isNewParticipant) return false;
      return false;
    }

    return false;
  };

  const resetCityState = () => {
    setCityChoices([]);
    setHasMultipleCities(false);
  };

  useEffect(() => {
    const id = initialFormValues?.[`${participantType}ParticipantId`];
    const name = initialFormValues?.[`${participantType}Name`];
    const newFlag = initialFormValues?.[`${participantType}NewParticipant`];
    const cp = initialFormValues?.[`${participantType}PostalCode`];

    const isExistingParticipant = !!id || (!!name && newFlag === false);
    const isFormReset = cp === null || cp === undefined || cp === "";

    if (disabled || isExistingParticipant || isFormReset) {
      resetCityState();
      setFieldsValue({
        [`${participantType}City`]: isExistingParticipant
          ? initialFormValues?.[`${participantType}City`] || null
          : null,
        [`${participantType}CityCode`]: isExistingParticipant
          ? initialFormValues?.[`${participantType}CityCode`] || null
          : null,
        [`${participantType}Country`]: isExistingParticipant
          ? initialFormValues?.[`${participantType}Country`] || null
          : null,
      });
    }
  }, [
    disabled,
    participantType,
    initialFormValues?.[`${participantType}ParticipantId`],
    initialFormValues?.[`${participantType}Name`],
    initialFormValues?.[`${participantType}NewParticipant`],
    initialFormValues?.[`${participantType}PostalCode`],
  ]);

  const handlePostCode = async (postCode) => {
    if (disabled) return false;

    if (isPreregister && !proximityService) {
      const canUseSpecialPostalCode = canUseSpecialPostCode(
        getFieldsValue,
        reduxUserProfiles,
        participantType,
      );
      const isSpecialPostalCode = checkSpecialDestination(postCode);
      const isSender = participantType === "sender";
      const isRecipient = participantType === "recipient";

      if (
        canUseSpecialPostalCode &&
        isSpecialPostalCode &&
        isRecipient &&
        isNewParticipant
      ) {
        dispatch(updateParticipantsDetails({ recipientPostalCode: postCode }));
      }

      if (!canUseSpecialPostalCode && isSpecialPostalCode) {
        setFieldsValue({
          [`${participantType}PostalCode`]: null,
          [`${participantType}City`]: null,
          [`${participantType}Country`]: null,
          [`${participantType}CityCode`]: null,
        });

        onPostalCodeValidated(participantType, null);
        dispatch(
          updateParticipantsDetails({ [`${participantType}PostalCode`]: null }),
        );

        notification.error({
          message: "Acción no permitida",
          description: isSender
            ? `No puede utilizar el código postal ${postCode} en el apartado 'Remitente'.`
            : `No tiene autorización para usar el código postal ${postCode}. Por favor, contacte con Negocio para que se le habilite el perfil necesario. Muchas gracias.`,
        });
        return false;
      }
    }

    if (isPreregister && proximityService) {
      const isSpecialPostalCode = checkSpecialDestination(postCode);
      const isRecipient = participantType === "recipient";

      if (isSpecialPostalCode && isRecipient && isNewParticipant) {
        dispatch(updateParticipantsDetails({ recipientPostalCode: postCode }));
      }
    }

    if (!postCode) {
      resetCityState();
      setFieldsValue({
        [`${participantType}City`]: null,
        [`${participantType}Country`]: null,
        [`${participantType}CityCode`]: null,
      });
      dispatch(
        updateParticipantsDetails({ [`${participantType}PostalCode`]: null }),
      );
      return false;
    }

    try {
      setIsLoading(true);
      externalLoadingSetter(true);

      const results = await getPostalCodeInfo(postCode);
      const list = Array.isArray(results) ? results : results ? [results] : [];

      if (!list || list.length === 0) {
        resetCityState();
        setFields({
          [`${participantType}PostalCode`]: {
            value: postCode,
            errors: [new Error("El código postal no existe.")],
          },
          [`${participantType}City`]: { value: null },
          [`${participantType}CityCode`]: { value: null },
          [`${participantType}Country`]: { value: null },
        });

        onPostalCodeValidated(participantType, null);
        dispatch(
          updateParticipantsDetails({ [`${participantType}PostalCode`]: null }),
        );
      } else if (list.length === 1) {
        resetCityState();
        const { city, cityCode, country } = list[0];

        setFieldsValue({
          [`${participantType}City`]: city,
          [`${participantType}Country`]: country,
          [`${participantType}CityCode`]: cityCode,
        });

        onPostalCodeValidated(participantType, postCode);
        dispatch(
          updateParticipantsDetails({
            [`${participantType}PostalCode`]: postCode,
          }),
        );
      } else {
        setCityChoices(
          list.map(({ city, cityCode, country }) => ({
            city,
            cityCode,
            country,
          })),
        );
        setHasMultipleCities(true);

        setFieldsValue({
          [`${participantType}City`]: undefined,
          [`${participantType}Country`]: undefined,
          [`${participantType}CityCode`]: undefined,
        });

        onPostalCodeValidated(participantType, postCode);
        dispatch(
          updateParticipantsDetails({
            [`${participantType}PostalCode`]: postCode,
          }),
        );
      }

      if (participantType === "sender" && proximityService) {
        const restrictionResult = await getPostalCodeRestriction(postCode);
        if (!restrictionResult || restrictionResult.length === 0) {
          notification.error({
            message: "Acción no permitida",
            description: `El código postal de remitente ${postCode} no permite realizar el servicio Frio Proximidad.`,
          });

          resetCityState();
          setFieldsValue({
            [`${participantType}PostalCode`]: undefined,
            [`${participantType}City`]: undefined,
            [`${participantType}Country`]: undefined,
            [`${participantType}CityCode`]: undefined,
          });
          onPostalCodeValidated(participantType, null);
          return false;
        }
      }

      if (participantType === "recipient" && proximityService) {
        const senderPostCode = reduxParticipantDetails.senderPostalCode;
        const checkPostalCode = await getPostalCodeRestriction(senderPostCode);

        const match = checkPostalCode?.find(
          (item) => item.destinationPostalCode === postCode,
        );
        if (!match) {
          notification.error({
            message: "Acción no permitida",
            description: `No está permitido realizar el servicio Frio Proximidad para el código postal ${postCode} desde el código postal de remitente.`,
          });

          resetCityState();
          setFieldsValue({
            [`${participantType}PostalCode`]: undefined,
            [`${participantType}City`]: undefined,
            [`${participantType}Country`]: undefined,
            [`${participantType}CityCode`]: undefined,
          });
          onPostalCodeValidated(participantType, null);
          return false;
        }
      }

      setIsLoading(false);
      externalLoadingSetter(false);
    } catch (error) {
      resetCityState();
      setFields({
        [`${participantType}PostalCode`]: {
          value: postCode,
          errors: [new Error("Error al obtener el código postal.")],
        },
        [`${participantType}City`]: { value: null },
        [`${participantType}CityCode`]: { value: null },
        [`${participantType}Country`]: { value: null },
      });
      onPostalCodeValidated(participantType, null);

      setIsLoading(false);
      externalLoadingSetter(false);
    }
  };
  const handleCitySelect = (cityCodeSelected) => {
    const selected = cityChoices.find((c) => c.cityCode === cityCodeSelected);
    if (!selected) return;
    const { city, cityCode, country } = selected;
    setFieldsValue({
      [`${participantType}City`]: city,
      [`${participantType}CityCode`]: cityCode,
      [`${participantType}Country`]: country,
    });
  };

  return (
    <Row
      className="PostalCodeFieldsBlock pr-grid-row"
      type="flex"
      justify="space-between"
      gutter={[16, 16]}
    >
      <Col xs={24} md={7} className="pr-col">
        <PostalCodeAsyncTextInput
          label="Código postal"
          name={`${participantType}PostalCode`}
          getFieldDecorator={getFieldDecorator}
          initialValue={initialFormValues?.[`${participantType}PostalCode`]}
          disabled={disablePostalCodeField(
            initialFormValues?.[`${participantType}PostalCode`],
            participantType,
          )}
          required
          requiredMessage="Campo obligatorio"
          callback={handlePostCode}
          loading={isLoading}
          placeholder="Ej: 21001"
        />
      </Col>

      <Col xs={24} md={7} className="pr-col pr-col-offset">
        {hasMultipleCities ? (
          <Form.Item label="Localidad">
            {getFieldDecorator(`${participantType}City`, {
              rules: [{ required: true, message: "Campo obligatorio" }],
            })(
              <Select
                showSearch={false}
                placeholder={
                  isLoading ? "Cargando..." : "Seleccione una localidad"
                }
                onChange={(value, option) => handleCitySelect(option.key)}
                disabled={isLoading || disabled}
                required
                requiredMessage="Campo obligatorio"
              >
                {cityChoices.map(({ city, cityCode }) => (
                  <Option key={cityCode} value={city}>
                    {city}
                  </Option>
                ))}
              </Select>,
            )}
          </Form.Item>
        ) : (
          <TextInput
            label="Localidad"
            name={`${participantType}City`}
            getFieldDecorator={getFieldDecorator}
            initialValue={initialFormValues?.[`${participantType}City`] ?? null}
            required
            requiredMessage="Campo obligatorio"
            disabled
            placeholder={
              isLoading
                ? "Cargando..."
                : "Campo no editable, introduzca código postal"
            }
          />
        )}
      </Col>

      <Col xs={24} md={7} className="pr-col pr-col-offset">
        <TextInput
          label="País"
          name={`${participantType}Country`}
          getFieldDecorator={getFieldDecorator}
          initialValue={initialFormValues?.[`${participantType}Country`]}
          required
          disabled
          requiredMessage="Campo obligatorio"
          placeholder={
            isLoading
              ? "Cargando..."
              : "Campo no editable, introduzca código postal"
          }
        />
      </Col>

      <Col xs={24} md={7} className="hidden-column pr-col pr-col-offset">
        <TextInput
          label={`${participantType}CityCode`}
          name={`${participantType}CityCode`}
          getFieldDecorator={getFieldDecorator}
          initialValue={initialFormValues?.[`${participantType}CityCode`]}
          disabled
        />
      </Col>
    </Row>
  );
}
